import { CardGrouping } from "modules/common/model/models";
import {
  DeckLocationEnum,
  MagicFormatEnum,
  OnePieceFormatEnum,
  RiftboundFormatEnum,
  type CardCodeEntry,
  type DeckCard,
} from "modules/decks/model/deck";
import {
  MTG_HAND_RULES,
  ONE_PIECE_HAND_RULES,
  RIFTBOUND_HAND_RULES,
  type GameHandRules,
} from "modules/decks/details/model/exampleHandRules";
import {
  decodeRiftboundDeckCode,
  isRiftboundDeckCode,
} from "modules/decks/utils/riftboundDeckCode";
import {
  commandZoneDecision,
  commandZoneSlots,
} from "modules/decks/utils/commandZoneSlots";

const BASIC_LAND_NAMES = [
  "Plains",
  "Island",
  "Swamp",
  "Mountain",
  "Forest",
] as const;

const COMMANDER_FORMATS = new Set<string>([
  MagicFormatEnum.Commander,
  MagicFormatEnum.Brawl,
  MagicFormatEnum.Oathbreaker,
]);

export type DeckProgressOp = "exact" | "min" | "max";

export interface DeckProgressSection {
  label: string;
  current: number;
  target?: number;
  op?: DeckProgressOp;
}

export interface DeckProgress {
  sections: DeckProgressSection[];
}

export interface DeckProgressInput {
  format?: string;
  cards: DeckCard[];
}

export interface DeckQuantityContext {
  format?: string;
  cardType?: string;
  deckCopyLimit?: DeckCard["deckCopyLimit"];
  cards: DeckCard[];
}

export interface DeckLocationOption {
  value: number;
  label: string;
}

export interface DeckLocationSelection {
  options: DeckLocationOption[];
  nextLocation?: DeckLocationEnum;
  disabled?: boolean;
}

export interface DeckLocationContext {
  cards: DeckCard[];
  format?: string;
  currentLocation: DeckLocationEnum;
}

export interface DeckImportHelp {
  intro: string;
  example: readonly string[];
  details?: string;
}

export type DeckBalanceVisualization =
  | "mana-production"
  | "resource-balance"
  | "color-spread";
export type DeckColorMode = "mana" | "domain" | "generic";
export type DeckIconToken =
  | "leader"
  | "character"
  | "event"
  | "stage"
  | "don"
  | "sideboard"
  | "considering"
  | "commander"
  | "companion";

export type DeckGroupIcon =
  | { kind: "type-symbol"; value: string }
  | { kind: "icon"; value: DeckIconToken };

export interface DeckGroupingOption {
  value: CardGrouping;
  label: string;
  detail?: string;
}

export type DeckGroupingOptions = readonly [
  DeckGroupingOption,
  ...DeckGroupingOption[],
];

export interface DeckImportAdapter {
  acceptsList: boolean;
  placeholder: string;
  showCheapest: boolean;
  help: DeckImportHelp;
  formatNotice: (format: string | undefined) => string | undefined;
  decodeCardCodes?: (list: string) => Promise<CardCodeEntry[] | null>;
}

export interface DeckGameAdapter {
  defaultFormat?: string;
  formats: readonly string[];
  formatOrder: readonly string[];
  formatLabel: (slug: string) => string;
  groupingOptions: DeckGroupingOptions;
  locationLabel: (location: DeckLocationEnum) => string;
  mainboardLocations: readonly DeckLocationEnum[];
  emptyStateText: string;
  importer: DeckImportAdapter;
  openingHand?: GameHandRules;
  maxCopies: (context: DeckQuantityContext) => number;
  isBasicResourceCard: (cardName: string) => boolean;
  buildProgress: (input: DeckProgressInput) => DeckProgress;
  addCardLocations: (context: DeckLocationContext) => DeckLocationSelection;
  addCardTypes: (location: DeckLocationEnum) => readonly string[] | undefined;
  shouldAutoSubmitCard: (
    format: string | undefined,
    location: DeckLocationEnum,
    cardName: string
  ) => boolean;
  showSetFilter: boolean;
  curveLabel: string;
  balanceVisualization?: DeckBalanceVisualization;
  colorMode: DeckColorMode;
  colorlessWhenEmpty: boolean;
  groupIcon: (
    location: DeckLocationEnum,
    groupTitle?: string
  ) => DeckGroupIcon | undefined;
}

const STANDARD_LOCATIONS = [
  DeckLocationEnum.Commander,
  DeckLocationEnum.Mainboard,
  DeckLocationEnum.Sideboard,
  DeckLocationEnum.Considering,
] as const;

const ONE_PIECE_LOCATIONS = [
  DeckLocationEnum.Commander,
  DeckLocationEnum.Mainboard,
  DeckLocationEnum.Don,
  DeckLocationEnum.Sideboard,
  DeckLocationEnum.Considering,
] as const;

const COMMON_GROUPING_OPTIONS = [
  { value: CardGrouping.FirstType, label: "Type", detail: "default" },
  { value: CardGrouping.ManaValue, label: "Cost Value", detail: "0, 1, 2…" },
] as const satisfies DeckGroupingOptions;

const NO_GROUPING_OPTION = {
  value: CardGrouping.None,
  label: "No Grouping",
} as const satisfies DeckGroupingOption;

function defineGroupingOptions<const T extends DeckGroupingOptions>(
  options: T
): T {
  const values = new Set<CardGrouping>();
  const labels = new Set<string>();

  for (const option of options) {
    const normalizedLabel = option.label.trim().toLocaleLowerCase();
    if (values.has(option.value) || labels.has(normalizedLabel)) {
      throw new Error(
        "Deck grouping options must have unique values and labels"
      );
    }
    values.add(option.value);
    labels.add(normalizedLabel);
  }

  return options;
}

const GENERIC_GROUPING_OPTIONS = defineGroupingOptions([
  ...COMMON_GROUPING_OPTIONS,
  NO_GROUPING_OPTION,
] as const);

const MTG_GROUPING_OPTIONS = defineGroupingOptions([
  ...COMMON_GROUPING_OPTIONS,
  { value: CardGrouping.Color, label: "Color", detail: "W U B R G" },
  { value: CardGrouping.ColorIdentity, label: "Color Identity" },
  NO_GROUPING_OPTION,
] as const);

const RIFTBOUND_GROUPING_OPTIONS = defineGroupingOptions([
  ...COMMON_GROUPING_OPTIONS,
  { value: CardGrouping.Color, label: "Domain", detail: "Mind, Fury…" },
  NO_GROUPING_OPTION,
] as const);

const ONE_PIECE_GROUPING_OPTIONS = defineGroupingOptions([
  ...COMMON_GROUPING_OPTIONS,
  { value: CardGrouping.Color, label: "Color", detail: "Red, Green…" },
  NO_GROUPING_OPTION,
] as const);

const DEFAULT_IMPORTER: DeckImportAdapter = {
  acceptsList: false,
  placeholder: "Enter your deck list...",
  showCheapest: false,
  help: {
    intro: "Import a deck list with a quantity and card name on each line.",
    example: ["Mainboard", "1 Card name"],
  },
  formatNotice: () => undefined,
};

const TEXT_IMPORTER: DeckImportAdapter = {
  acceptsList: true,
  placeholder: "Enter your deck list...",
  showCheapest: true,
  help: {
    intro: "Import a deck in the form of:",
    example: ["Mainboard", "1 Lightning Bolt", "Sideboard", "1 Goblin Guide"],
    details: "Locations such as Commander and Considering are also supported.",
  },
  formatNotice: (format) => {
    if (format === MagicFormatEnum.Oathbreaker) {
      return "Oathbreaker decks need a Commander section — the Oathbreaker and its Signature Spell are not detected automatically.";
    }
    // Brawl promotes exactly one commander and never seats a pair, so it
    // must not promise the Commander pairing behavior.
    if (format === MagicFormatEnum.Brawl) {
      return "If no Commander section is provided, the first card of the list is used as the commander.";
    }
    return COMMANDER_FORMATS.has(format ?? "")
      ? "If no Commander section is provided, the cards at the top of the list are used as the command zone — a commander, or a legal pair such as Partner commanders, a commander with its Background, or a Doctor with its companion."
      : undefined;
  },
};

const ONE_PIECE_IMPORTER: DeckImportAdapter = {
  acceptsList: true,
  placeholder: "Paste a One Piece deck list (e.g. from Limitless or Egman)...",
  showCheapest: false,
  help: {
    intro:
      "Paste a deck list from Limitless, Egman, OPTCGSIM, or Bandai's builder using quantity and Bandai code per line:",
    example: ["1 OP01-001", "4 OP01-006", "4 ST10-005", "2 EB02-014"],
    details:
      "Leader, Mainboard, and DON!! section headers are honored when present. Leader and DON!! cards are also classified automatically by type.",
  },
  formatNotice: () => undefined,
};

const RIFTBOUND_IMPORTER: DeckImportAdapter = {
  acceptsList: true,
  placeholder: "Paste a deck code or text list...",
  showCheapest: false,
  help: {
    intro: "Paste a deck code from Piltover Archive or a sectioned text list:",
    example: [
      "Legend",
      "1 Jinx, the Loose Cannon",
      "Mainboard",
      "3 Get Excited!",
      "Battlefield",
      "1 Piltover",
      "Rune",
      "6 Chaos Rune",
    ],
    details:
      "Supported sections are Legend, Mainboard, Battlefield, Rune, and Sideboard.",
  },
  formatNotice: () => undefined,
  decodeCardCodes: async (list) =>
    isRiftboundDeckCode(list) ? decodeRiftboundDeckCode(list) : null,
};

const MTG_FORMAT_ORDER: readonly MagicFormatEnum[] = [
  MagicFormatEnum.Commander,
  MagicFormatEnum.Modern,
  MagicFormatEnum.Pauper,
  MagicFormatEnum.Legacy,
  MagicFormatEnum.Standard,
  MagicFormatEnum.Pioneer,
  MagicFormatEnum.Vintage,
  MagicFormatEnum.Historic,
  MagicFormatEnum.Brawl,
  MagicFormatEnum.Duel,
  MagicFormatEnum.Penny,
  MagicFormatEnum.Frontier,
  MagicFormatEnum.Future,
];

function titleCase(slug: string): string {
  return slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : "";
}

function genericLocationLabel(location: DeckLocationEnum): string {
  const labels: Record<DeckLocationEnum, string> = {
    [DeckLocationEnum.Mainboard]: "Mainboard",
    [DeckLocationEnum.Sideboard]: "Sideboard",
    [DeckLocationEnum.Companion]: "Companion",
    [DeckLocationEnum.Commander]: "Leader",
    [DeckLocationEnum.Considering]: "Considering",
    [DeckLocationEnum.Don]: "DON!! Pile",
  };
  return labels[location];
}

function locationOptions(
  locations: readonly DeckLocationEnum[],
  label: (location: DeckLocationEnum) => string
): DeckLocationOption[] {
  return locations.map((location) => ({
    value: Number(location),
    label: label(location),
  }));
}

function sumByLocation(cards: DeckCard[], location: DeckLocationEnum): number {
  return cards
    .filter((card) => card.location === location)
    .reduce((sum, card) => sum + card.quantity, 0);
}

function sumByType(cards: DeckCard[], type: string): number {
  return cards
    .filter((card) => card.types.includes(type))
    .reduce((sum, card) => sum + card.quantity, 0);
}

function genericProgress({ cards }: DeckProgressInput): DeckProgress {
  const mainboard = sumByLocation(cards, DeckLocationEnum.Mainboard);
  const sideboard = sumByLocation(cards, DeckLocationEnum.Sideboard);
  return {
    sections: [
      { label: "Mainboard", current: mainboard },
      ...(sideboard > 0 ? [{ label: "Sideboard", current: sideboard }] : []),
    ],
  };
}

function mtgProgress({ cards, format }: DeckProgressInput): DeckProgress {
  const mainboard = sumByLocation(cards, DeckLocationEnum.Mainboard);
  const sideboard = sumByLocation(cards, DeckLocationEnum.Sideboard);
  const commanders = sumByLocation(cards, DeckLocationEnum.Commander);
  const sections: DeckProgressSection[] = [];

  switch (format) {
    case MagicFormatEnum.Commander:
      sections.push({
        label: "Mainboard",
        current: mainboard,
        target: Math.max(0, 100 - commanders),
        op: "exact",
      });
      sections.push({
        label: commanders === 2 ? "Commanders" : "Commander",
        current: commanders,
      });
      break;
    case MagicFormatEnum.Brawl:
      sections.push({
        label: "Mainboard",
        current: mainboard,
        target: Math.max(0, 60 - commanders),
        op: "exact",
      });
      sections.push({ label: "Commander", current: commanders });
      break;
    case MagicFormatEnum.Oathbreaker:
      sections.push({
        label: "Mainboard",
        current: mainboard,
        target: 58,
        op: "exact",
      });
      sections.push({
        label: "Command zone",
        current: commanders,
        target: 2,
        op: "exact",
      });
      break;
    default:
      sections.push({
        label: "Mainboard",
        current: mainboard,
        target: 60,
        op: "min",
      });
  }

  if (sideboard > 0) {
    sections.push({
      label: "Sideboard",
      current: sideboard,
      target: 15,
      op: "max",
    });
  }
  return { sections };
}

function riftboundProgress({ cards }: DeckProgressInput): DeckProgress {
  // API Legend()/BattlefieldsCount()/RunesCount() are type-based. Imports and
  // older decks store those cards on the mainboard; the add-card UI may also
  // put a legend in CommanderLoc. Count by type so both shapes agree.
  const legends = sumByType(cards, "Legend");
  const battlefields = sumByType(cards, "Battlefield");
  const runes = sumByType(cards, "Rune");
  const champions = cards
    .filter(
      (card) =>
        card.location === DeckLocationEnum.Mainboard &&
        !card.types.includes("Legend") &&
        !card.types.includes("Battlefield") &&
        !card.types.includes("Rune")
    )
    .reduce((sum, card) => sum + card.quantity, 0);
  return {
    sections: [
      {
        label: "Mainboard",
        current: champions,
        target: 40,
        op: "min",
      },
      {
        label: "Legend",
        current: legends,
        target: 1,
        op: "exact",
      },
      {
        label: "Battlefields",
        current: battlefields,
        target: 3,
        op: "exact",
      },
      { label: "Runes", current: runes, target: 12, op: "max" },
    ],
  };
}

function onePieceProgress({ cards }: DeckProgressInput): DeckProgress {
  return {
    sections: [
      {
        label: "Mainboard",
        current: sumByLocation(cards, DeckLocationEnum.Mainboard),
        target: 50,
        op: "exact",
      },
      {
        label: "Leader",
        current: sumByLocation(cards, DeckLocationEnum.Commander),
        target: 1,
        op: "exact",
      },
    ],
  };
}

function standardAddCardLocations(
  _context: DeckLocationContext
): DeckLocationSelection {
  const options = [
    DeckLocationEnum.Mainboard,
    DeckLocationEnum.Sideboard,
    DeckLocationEnum.Companion,
    DeckLocationEnum.Considering,
  ];
  return { options: locationOptions(options, genericLocationLabel) };
}

function mtgAddCardLocations(
  context: DeckLocationContext
): DeckLocationSelection {
  const ordinary = [
    DeckLocationEnum.Mainboard,
    DeckLocationEnum.Sideboard,
    DeckLocationEnum.Companion,
    DeckLocationEnum.Considering,
  ];
  if (!COMMANDER_FORMATS.has(context.format ?? "")) {
    return { options: locationOptions(ordinary, mtgLocationLabel) };
  }
  const commanderCount = context.cards.filter(
    (card) => card.location === DeckLocationEnum.Commander
  ).length;
  const { offerCommander, nextLocation, disabled } = commandZoneDecision({
    commanderCount,
    slots: commandZoneSlots(context.format ?? ""),
    currentLocation: context.currentLocation,
    commanderLocation: DeckLocationEnum.Commander,
    mainboardLocation: DeckLocationEnum.Mainboard,
  });
  const options = locationOptions(
    offerCommander ? [DeckLocationEnum.Commander, ...ordinary] : ordinary,
    mtgLocationLabel
  );
  return { options, nextLocation, disabled };
}

function leaderFirstLocations(
  context: DeckLocationContext,
  options: readonly DeckLocationEnum[],
  label: (location: DeckLocationEnum) => string,
  isLeader: (card: DeckCard) => boolean = (card) =>
    card.location === DeckLocationEnum.Commander
): DeckLocationSelection {
  const hasLeader = context.cards.some(isLeader);
  return {
    options: locationOptions(options, label),
    nextLocation: !hasLeader
      ? DeckLocationEnum.Commander
      : context.currentLocation === DeckLocationEnum.Commander
        ? DeckLocationEnum.Mainboard
        : undefined,
  };
}

function mtgLocationLabel(location: DeckLocationEnum): string {
  return location === DeckLocationEnum.Commander
    ? "Commander"
    : genericLocationLabel(location);
}

function riftboundLocationLabel(location: DeckLocationEnum): string {
  if (location === DeckLocationEnum.Mainboard) return "Champions";
  if (location === DeckLocationEnum.Commander) return "Legend";
  return genericLocationLabel(location);
}

function onePieceLocationLabel(location: DeckLocationEnum): string {
  return location === DeckLocationEnum.Commander
    ? "Leader"
    : genericLocationLabel(location);
}

function neverAutoSubmit(): boolean {
  return false;
}

function genericGroupIcon(
  location: DeckLocationEnum,
  groupTitle?: string
): DeckGroupIcon | undefined {
  switch (location) {
    case DeckLocationEnum.Mainboard:
      return groupTitle
        ? { kind: "type-symbol", value: groupTitle }
        : undefined;
    case DeckLocationEnum.Sideboard:
      return { kind: "icon", value: "sideboard" };
    case DeckLocationEnum.Considering:
      return { kind: "icon", value: "considering" };
    case DeckLocationEnum.Commander:
      return { kind: "icon", value: "commander" };
    case DeckLocationEnum.Companion:
      return { kind: "icon", value: "companion" };
    default:
      return undefined;
  }
}

function onePieceGroupIcon(
  location: DeckLocationEnum,
  groupTitle?: string
): DeckGroupIcon | undefined {
  if (location === DeckLocationEnum.Commander) {
    return { kind: "icon", value: "leader" };
  }
  if (location === DeckLocationEnum.Don) {
    return { kind: "icon", value: "don" };
  }
  if (location !== DeckLocationEnum.Mainboard || !groupTitle) {
    return genericGroupIcon(location, groupTitle);
  }

  const iconByType: Partial<Record<string, DeckIconToken>> = {
    Leader: "leader",
    Character: "character",
    Event: "event",
    Stage: "stage",
    "DON!!": "don",
    "DON!! Pile": "don",
  };
  const icon = iconByType[groupTitle];
  return icon
    ? { kind: "icon", value: icon }
    : { kind: "type-symbol", value: groupTitle };
}

function isBasicLandName(name: string): boolean {
  const normalized = name.trim().replace(/^Snow-Covered\s+/i, "");
  return (
    normalized === "Wastes" ||
    BASIC_LAND_NAMES.includes(normalized as (typeof BASIC_LAND_NAMES)[number])
  );
}

export const GENERIC_DECK_ADAPTER: DeckGameAdapter = {
  formats: [],
  formatOrder: [],
  formatLabel: titleCase,
  groupingOptions: GENERIC_GROUPING_OPTIONS,
  locationLabel: genericLocationLabel,
  mainboardLocations: STANDARD_LOCATIONS,
  emptyStateText: "Add some cards",
  importer: DEFAULT_IMPORTER,
  maxCopies: () => Number.POSITIVE_INFINITY,
  isBasicResourceCard: () => false,
  buildProgress: genericProgress,
  addCardLocations: standardAddCardLocations,
  addCardTypes: () => undefined,
  shouldAutoSubmitCard: neverAutoSubmit,
  showSetFilter: false,
  curveLabel: "Cost curve",
  colorMode: "generic",
  colorlessWhenEmpty: false,
  groupIcon: genericGroupIcon,
};

export const MTG_DECK_ADAPTER: DeckGameAdapter = {
  ...GENERIC_DECK_ADAPTER,
  defaultFormat: MagicFormatEnum.Standard,
  formats: Object.values(MagicFormatEnum),
  formatOrder: MTG_FORMAT_ORDER,
  groupingOptions: MTG_GROUPING_OPTIONS,
  locationLabel: mtgLocationLabel,
  importer: TEXT_IMPORTER,
  openingHand: MTG_HAND_RULES,
  maxCopies: ({ format, deckCopyLimit }) => {
    if (deckCopyLimit) {
      return deckCopyLimit.max ?? Number.POSITIVE_INFINITY;
    }
    return COMMANDER_FORMATS.has(format ?? "") ? 1 : 4;
  },
  isBasicResourceCard: isBasicLandName,
  buildProgress: mtgProgress,
  addCardLocations: mtgAddCardLocations,
  shouldAutoSubmitCard: (format, location, cardName) =>
    COMMANDER_FORMATS.has(format ?? "") &&
    location !== DeckLocationEnum.Sideboard &&
    !isBasicLandName(cardName),
  showSetFilter: true,
  curveLabel: "Mana curve",
  balanceVisualization: "mana-production",
  colorMode: "mana",
  colorlessWhenEmpty: true,
};

export const RIFTBOUND_DECK_ADAPTER: DeckGameAdapter = {
  ...GENERIC_DECK_ADAPTER,
  defaultFormat: RiftboundFormatEnum.Constructed,
  formats: Object.values(RiftboundFormatEnum),
  formatOrder: Object.values(RiftboundFormatEnum),
  formatLabel: (slug) =>
    slug === RiftboundFormatEnum.Constructed ? "Constructed" : titleCase(slug),
  groupingOptions: RIFTBOUND_GROUPING_OPTIONS,
  locationLabel: riftboundLocationLabel,
  emptyStateText: "Add your Legend to begin building",
  importer: RIFTBOUND_IMPORTER,
  openingHand: RIFTBOUND_HAND_RULES,
  maxCopies: ({ cards, cardType }) => {
    if (cardType === "Legend") return 1;
    if (cardType !== "Rune") return 3;
    const runeCount = cards
      .filter((card) => card.types.includes("Rune"))
      .reduce((sum, card) => sum + card.quantity, 0);
    return runeCount === 12 ? 0 : 12;
  },
  buildProgress: riftboundProgress,
  addCardLocations: (context) =>
    leaderFirstLocations(
      context,
      [
        DeckLocationEnum.Commander,
        DeckLocationEnum.Mainboard,
        DeckLocationEnum.Considering,
      ],
      riftboundLocationLabel,
      (card) =>
        card.location === DeckLocationEnum.Commander ||
        card.types.includes("Legend")
    ),
  addCardTypes: (location) =>
    location === DeckLocationEnum.Commander ? ["Legend"] : undefined,
  curveLabel: "Cost curve",
  balanceVisualization: "resource-balance",
  colorMode: "domain",
};

export const ONE_PIECE_DECK_ADAPTER: DeckGameAdapter = {
  ...GENERIC_DECK_ADAPTER,
  defaultFormat: OnePieceFormatEnum.Standard,
  formats: Object.values(OnePieceFormatEnum),
  formatOrder: Object.values(OnePieceFormatEnum),
  groupingOptions: ONE_PIECE_GROUPING_OPTIONS,
  locationLabel: onePieceLocationLabel,
  mainboardLocations: ONE_PIECE_LOCATIONS,
  emptyStateText: "Add your Leader to begin building",
  importer: ONE_PIECE_IMPORTER,
  openingHand: ONE_PIECE_HAND_RULES,
  maxCopies: () => 4,
  buildProgress: onePieceProgress,
  addCardLocations: (context) =>
    leaderFirstLocations(
      context,
      [
        DeckLocationEnum.Commander,
        DeckLocationEnum.Mainboard,
        DeckLocationEnum.Don,
        DeckLocationEnum.Considering,
      ],
      onePieceLocationLabel
    ),
  addCardTypes: (location) => {
    if (location === DeckLocationEnum.Commander) return ["Leader"];
    return location === DeckLocationEnum.Don ? ["DON!!"] : undefined;
  },
  balanceVisualization: "color-spread",
  groupIcon: onePieceGroupIcon,
};
